/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Injectable }            from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import { User       }            from '@/models/user';
import { AuthenticationService } from "@/services/authentication.service";
import {IOrganizationRow} from "@/screens/administration/i.organization.row";


@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class OrganizationService
 *
 * @brief CRUD access to organization models
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2022
 *
 */
export class OrganizationService {

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService
  ) {


  }

  /** fetch the list of organizations */
  getAll() {

    return this.http.get<IOrganizationRow[]>(`/backend/organizations/all`);
  }

  /** find a specific organization (by OID) */
  getById(id: number) {

    return this.http.get(`/backend/organizations/list/${id}`);
  }

  /** fetch any headers we need to send along with our request */
  headers(): HttpHeaders {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return new HttpHeaders().set('Authorization', user.token).set('Content-Type', 'application/json');
  }

  /** helper to just get a simple CSV style dump */
  dump(orgid: string = '') {

    if (orgid.length != 0) {

      return this.http.get(`/backend/report/organizationdump/${orgid}`);
    }

    return this.http.get(`/backend/report/organizationdump`);
  }

  /** delete an organization, we don't actually delete, we just mark as deleted. */
  deleteOrganization(orgid: number|string) {

    const upd: IOrganizationRow = {
      is_deleted: true
    };

    return this.update(orgid, upd);
  }

  /** undelete an organization */
  undeleteOrganization(orgid: number|string) {

    const upd: IOrganizationRow = {
      is_deleted: false
    };

    return this.update(orgid, upd);
  }

  /** update a organization */
  update(orgid: number|string, data: IOrganizationRow) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.post(`/backend/organizations/edit/${orgid}`, data, {
      headers
    });
  }

  /** create an organization */
  create(data: IOrganizationRow) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.post(`/backend/organizations/create`, data, {
      headers
    });
  }

  /** helper for userOrganizations() on self */
  myOrganizations() {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return this.userOrganizations(user.username);
  }

  /** fetch the organizations that this user has access to (as an admin) */
  userOrganizations(userid: string) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.get(`/backend/user/organizations/${userid}`, {
      headers
    });
  }

  /** link the organization (oid) to the subdivision (sid) */
  linkSubdivision(oid: number, sid: number) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.put(`/backend/org/sub/link/${oid}/${sid}`, {
      headers
    });
  }

  /** unlink the organization (oid) from the subdivision (sid) */
  unlinkSubdivision(oid: number, sid: number) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.put(`/backend/org/sub/unlink/${oid}/${sid}`, {
      headers
    });
  }

}
